import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSCheckBox, ELSIcon } from 'components/common/els';
import { DropdownAction } from './constants';

export interface PupilSizeDropdownProps {
  id: string;
  label: string;
  placeholder: string;
  sizeFormField: ChartMetaFormField;
  roundFormField: ChartMetaFormField;
  isRequired?: boolean;
  disabled?: boolean;
}

const fireSizeChange = (sizeFormField: ChartMetaFormField, value: string): void => {
  if (!sizeFormField.onChange) return;
  const event = { target: { name: sizeFormField.name, value } } as unknown as React.ChangeEvent<HTMLInputElement>;
  sizeFormField.onChange(sizeFormField, event);
};

const fireRoundToggle = (roundFormField: ChartMetaFormField, roundItem: ContentItem): void => {
  if (!roundItem.onChange) return;
  roundItem.onChange(roundFormField, roundItem);
};

interface PanelProps {
  id: string;
  label: string;
  isRound: boolean;
  selectedValue: string;
  sizeOptions: ContentItem[];
  onClear: () => void;
  onRoundToggle: () => void;
  onSizeSelect: (value: string) => void;
}

const PupilSizePanel = ({ id, label, isRound, selectedValue, sizeOptions, onClear, onRoundToggle, onSizeSelect }: PanelProps) => {
  const checkboxInputId = `field-input-${id}-round`;
  return (
    <div aria-label={label} className="pupil-size-dropdown__panel">
      <button type="button" className="pupil-size-dropdown__option pupil-size-dropdown__clear" onClick={onClear}>
        {DropdownAction.CLEAR_LABEL}
      </button>

      <div className="pupil-size-dropdown__round">
        <ELSCheckBox id={`${id}-round`} value="round" checked={isRound} changeHandler={onRoundToggle}>
          <label htmlFor={checkboxInputId}>{DropdownAction.ROUND_LABEL}</label>
        </ELSCheckBox>
      </div>

      {sizeOptions.map((item) => {
        const isSelected = item.value === selectedValue;
        return (
          <button
            key={item.id ?? `${item.value}`}
            type="button"
            disabled={!isRound}
            className={cx('pupil-size-dropdown__option pupil-size-dropdown__size-option', {
              'pupil-size-dropdown__option--selected': isSelected,
              'pupil-size-dropdown__option--disabled': !isRound
            })}
            onClick={() => onSizeSelect(item.value as string)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

const useDismissOnOutsideOrEscape = (isOpen: boolean, containerRef: React.RefObject<HTMLElement>, close: () => void) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMousedown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) close();
    };
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleMousedown);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleMousedown);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isOpen, containerRef, close]);
};

const buildHandlers = (sizeFormField: ChartMetaFormField, roundFormField: ChartMetaFormField, roundItem: ContentItem | undefined, isRound: boolean, close: () => void) => {
  const handleClear = () => {
    if (sizeFormField.value) fireSizeChange(sizeFormField, '');
    if (isRound && roundItem) fireRoundToggle(roundFormField, roundItem);
    close();
  };
  const handleRoundChange = () => {
    if (!roundItem) return;
    fireRoundToggle(roundFormField, roundItem);
    if (isRound && sizeFormField.value) fireSizeChange(sizeFormField, '');
  };
  const handleSizeSelect = (value: string) => {
    if (!isRound) return;
    fireSizeChange(sizeFormField, value);
    close();
  };
  return { handleClear, handleRoundChange, handleSizeSelect };
};

const PupilSizeDropdown = ({ id, label, placeholder, sizeFormField, roundFormField, isRequired, disabled }: PupilSizeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setIsOpen(false), []);
  useDismissOnOutsideOrEscape(isOpen, containerRef, close);

  if (!sizeFormField || !roundFormField) {
    return null;
  }

  const roundItem = roundFormField.chartContent?.[0];
  const isRound = !!roundItem?.selected;
  const sizeOptions = (sizeFormField.chartContent ?? []).filter((item) => item.value !== '' && !item.isHidden);
  const selectedSize = sizeOptions.find((item) => item.value === sizeFormField.value);
  const { handleClear, handleRoundChange, handleSizeSelect } = buildHandlers(sizeFormField, roundFormField, roundItem, isRound, close);

  const triggerId = `field-input-${id}`;

  return (
    <div className="chart-input__field pupil-size-dropdown" ref={containerRef}>
      <div className="chart-input__label pupil-size-dropdown__label">
        {isRequired && <span className="chart-input__label-asterisk">* </span>}
        <label htmlFor={triggerId}>{label}</label>
      </div>
      <div className="chart-input__dropdown pupil-size-dropdown__field">
        <div className="c-els-field">
          <span className="c-els-field__wrap c-els-field__wrap--icon-right">
            <ELSIcon name="chevron-down" prefix="hmds" direction="right" size="1x1o2" />
            <button
              id={triggerId}
              type="button"
              aria-haspopup="true"
              aria-expanded={isOpen}
              aria-label={label}
              disabled={disabled}
              className={cx('c-els-field__input pupil-size-dropdown__trigger', { 'pupil-size-dropdown__trigger--open': isOpen })}
              onClick={() => setIsOpen((open) => !open)}
            >
              <span className={cx('pupil-size-dropdown__value', { 'pupil-size-dropdown__placeholder': !selectedSize })}>{selectedSize ? selectedSize.label : placeholder}</span>
            </button>
          </span>
        </div>
        {isOpen && (
          <PupilSizePanel
            id={id}
            label={label}
            isRound={isRound}
            selectedValue={(sizeFormField.value as string) ?? ''}
            sizeOptions={sizeOptions}
            onClear={handleClear}
            onRoundToggle={handleRoundChange}
            onSizeSelect={handleSizeSelect}
          />
        )}
      </div>
    </div>
  );
};

PupilSizeDropdown.displayName = 'PupilSizeDropdown';

export default PupilSizeDropdown;
